// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data, continuous sources (docs/AYNI_DATA_INTAKE_PLAN.md,
 * "Accepting and uploading → Continuous sources", phase 3; contract
 * docs/AYNI_DATA_INTAKE_API.md section 19): what a customer gives for an
 * S3-compatible bucket we read on a schedule, checked for shape. Shared by
 * the XR server and the browser's form. No DOM, no Worker globals.
 *
 * **Server-side request forgery.** The XR server never fetches the
 * customer's endpoint; only the intake server does, through its egress
 * proxy (https only, the name resolved once, private, loopback,
 * link-local and metadata addresses refused, redirects checked again).
 * What is checked here is the string alone, so an obviously internal
 * address is refused before it reaches anyone: `https:` only, no user or
 * password, no path, query or fragment, a proper DNS name with a dot (no
 * `localhost`, `.local`, `.internal`, `.lan`, `.home.arpa`), and an IP
 * literal only when it is a public address. A name that resolves to a
 * private address is the egress proxy's to refuse (it resolves names; we
 * must not).
 */

import { parseJobChoices, type JobChoices } from './ownData'

/** A source's poll interval, minutes (the plan: every 5 minutes at most). */
export const SOURCE_MIN_POLL_MINUTES = 5
export const SOURCE_MAX_POLL_MINUTES = 24 * 60
/** The most recent frames a rolling window may keep. */
export const SOURCE_MAX_WINDOW_FRAMES = 10_000

export type SourceState = 'testing' | 'active' | 'paused' | 'broken' | 'refused' | 'deleted'
export const SOURCE_STATES: readonly SourceState[] = ['testing', 'active', 'paused', 'broken', 'refused', 'deleted']
/** States that count against the plan's number of continuous sources. */
export const COUNTED_SOURCE_STATES: readonly SourceState[] = ['testing', 'active', 'paused', 'broken']

/** What a source's alert is about (contract section 19.4). */
export const SOURCE_ALERT_KINDS = ['drift', 'credentials', 'writable', 'unreachable', 'missed', 'unreadable', 'quota', 'variable_missing'] as const
export type SourceAlertKind = (typeof SOURCE_ALERT_KINDS)[number]

export type SourceSchedule = { kind: 'poll'; minutes: number } | { kind: 'notify' }
export type SourceWindow = { kind: 'recent'; frames: number } | { kind: 'archive' }

export interface SourceInput {
  title: string
  endpoint: string
  bucket: string
  prefix: string
  region: string
  accessKeyId: string
  secretAccessKey: string
  schedule: SourceSchedule
  window: SourceWindow
  choices: JobChoices
}

export type EndpointProblem = 'not_url' | 'not_https' | 'credentials_in_url' | 'path_not_allowed' | 'bad_host' | 'internal_host' | 'private_address' | 'bad_port'

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/** An IPv4 address's four numbers, or null. */
function ipv4(host: string): [number, number, number, number] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host)
  if (!m) return null
  const n = m.slice(1).map(Number) as [number, number, number, number]
  return n.every((x) => x <= 255) ? n : null
}

/** Whether an IPv4 address is private, loopback, link-local, shared, reserved, documentation or multicast. */
export function privateIpv4([a, b, c]: [number, number, number, number]): boolean {
  return a === 0 || a === 10 || a === 127 || a >= 224
    || (a === 100 && b >= 64 && b <= 127)
    || (a === 169 && b === 254)
    || (a === 172 && b >= 16 && b <= 31)
    || (a === 192 && b === 168)
    || (a === 192 && b === 0 && (c === 0 || c === 2))
    || (a === 198 && (b === 18 || b === 19))
    || (a === 198 && b === 51 && c === 100)
    || (a === 203 && b === 0 && c === 113)
}

/** Whether a bracketed IPv6 literal (as `URL.hostname` gives it) is not a public unicast address. */
export function privateIpv6(host: string): boolean {
  const h = host.replace(/^\[|\]$/g, '').toLowerCase()
  if (h === '::' || h === '::1') return true
  // IPv4-mapped and -translated forms, and anything with an embedded IPv4 tail: refused rather than parsed.
  if (h.includes('.') || h.startsWith('::ffff:') || h.startsWith('64:ff9b:')) return true
  const first = parseInt(h.split(':')[0] || '0', 16)
  if (!Number.isFinite(first)) return true
  return (first & 0xfe00) === 0xfc00 // fc00::/7 unique local
    || (first & 0xffc0) === 0xfe80 // fe80::/10 link-local
    || (first & 0xff00) === 0xff00 // multicast
    || first === 0x2001 && parseInt(h.split(':')[1] || '0', 16) === 0x0db8 // documentation
    || first === 0 // ::/8 and the rest of the reserved low range
}

const INTERNAL_SUFFIXES = ['.localhost', '.local', '.internal', '.lan', '.home.arpa', '.intranet', '.localdomain']
const LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/

/**
 * Why an S3 endpoint address is refused, or null when its shape is fine
 * (the egress proxy still checks where the name resolves).
 */
export function endpointProblem(raw: unknown): EndpointProblem | null {
  if (typeof raw !== 'string' || raw.length > 300) return 'not_url'
  let u: URL
  try {
    u = new URL(raw.trim())
  } catch {
    return 'not_url'
  }
  if (u.protocol !== 'https:') return 'not_https'
  if (u.username || u.password) return 'credentials_in_url'
  if ((u.pathname !== '/' && u.pathname !== '') || u.search || u.hash) return 'path_not_allowed'
  if (u.port && !(u.port === '443' || Number(u.port) >= 1024)) return 'bad_port'
  const host = u.hostname.toLowerCase()
  if (host.startsWith('[')) return privateIpv6(host) ? 'private_address' : null
  const v4 = ipv4(host)
  if (v4) return privateIpv4(v4) ? 'private_address' : null
  // The URL parser turns 0x7f.1 and 2130706433 into dotted form; anything else numeric-looking is not a name.
  if (/^[0-9.]+$/.test(host)) return 'bad_host'
  if (host === 'localhost' || INTERNAL_SUFFIXES.some((s) => host.endsWith(s))) return 'internal_host'
  const labels = host.replace(/\.$/, '').split('.')
  if (labels.length < 2 || host.length > 253 || !labels.every((l) => LABEL.test(l)) || /^[0-9]+$/.test(labels[labels.length - 1])) return 'bad_host'
  return null
}

/** The endpoint as stored and sent: origin only, lower-case host, no trailing slash. */
export function normaliseEndpoint(raw: string): string {
  return new URL(raw.trim()).origin
}

/** An S3 bucket name (the common rules: 3–63 of a-z 0-9 . -, not an address, no `..`). */
export function validBucket(v: unknown): v is string {
  return typeof v === 'string' && /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(v) && !v.includes('..') && !/^\d+\.\d+\.\d+\.\d+$/.test(v) && !v.startsWith('xn--')
}

/** A key prefix: empty (the whole bucket) or up to 512 characters, no control characters, no leading `/`, no `.`/`..` segments, no `\`. */
export function validPrefix(v: unknown): v is string {
  if (typeof v !== 'string' || v.length > 512) return false
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(v) || v.startsWith('/')) return false
  return !v.split('/').some((s) => s === '.' || s === '..')
}

export const validRegion = (v: unknown): v is string => typeof v === 'string' && /^[a-z0-9-]{2,32}$/.test(v)
export const validAccessKeyId = (v: unknown): v is string => typeof v === 'string' && /^[A-Za-z0-9_+=/.-]{8,128}$/.test(v)
export const validSecret = (v: unknown): v is string => typeof v === 'string' && /^[\x21-\x7e]{8,256}$/.test(v)

const VARIABLE = /^[\p{L}\p{N}_.:\-/ ]{1,128}$/u

/**
 * A variable name as given (a NetCDF group path may hold `/`), but never a
 * path that could climb (review I-8): no leading `/`, no `.`, `..` or empty
 * segment. The intake server must still never use it as a file path, and
 * must URL-encode the prefix (contract section 19.2).
 */
export function validVariable(v: string): boolean {
  return VARIABLE.test(v) && !v.startsWith('/') && !v.split('/').some((seg) => seg === '.' || seg === '..' || seg.trim() === '')
}

export type SourceInputError =
  | { field: 'endpoint'; problem: EndpointProblem }
  | { field: 'title' | 'bucket' | 'prefix' | 'region' | 'accessKeyId' | 'secretAccessKey' | 'schedule' | 'window' | 'choices'; problem: 'invalid' }

/** A continuous source as given in a form or a request, checked; the endpoint normalised to its origin. */
export function parseSourceInput(input: unknown): { ok: true; value: SourceInput } | { ok: false; error: SourceInputError } {
  const bad = (field: Exclude<SourceInputError['field'], 'endpoint'>) => ({ ok: false as const, error: { field, problem: 'invalid' as const } })
  if (!isRecord(input)) return { ok: false, error: { field: 'endpoint', problem: 'not_url' } }
  const title = typeof input.title === 'string' ? input.title.replace(/[\u0000-\u001f\u007f]/g, '').trim() : '' // eslint-disable-line no-control-regex
  if (!title || title.length > 200) return bad('title')
  const problem = endpointProblem(input.endpoint)
  if (problem) return { ok: false, error: { field: 'endpoint', problem } }
  if (!validBucket(input.bucket)) return bad('bucket')
  const prefix = input.prefix ?? ''
  if (!validPrefix(prefix)) return bad('prefix')
  const region = input.region ?? 'auto'
  if (!validRegion(region)) return bad('region')
  if (!validAccessKeyId(input.accessKeyId)) return bad('accessKeyId')
  if (!validSecret(input.secretAccessKey)) return bad('secretAccessKey')
  const s = input.schedule
  let schedule: SourceSchedule
  if (isRecord(s) && s.kind === 'notify') schedule = { kind: 'notify' }
  else if (isRecord(s) && s.kind === 'poll' && typeof s.minutes === 'number' && Number.isInteger(s.minutes) && s.minutes >= SOURCE_MIN_POLL_MINUTES && s.minutes <= SOURCE_MAX_POLL_MINUTES) schedule = { kind: 'poll', minutes: s.minutes }
  else return bad('schedule')
  const w = input.window
  let window: SourceWindow
  if (isRecord(w) && w.kind === 'archive') window = { kind: 'archive' }
  else if (isRecord(w) && w.kind === 'recent' && typeof w.frames === 'number' && Number.isInteger(w.frames) && w.frames >= 1 && w.frames <= SOURCE_MAX_WINDOW_FRAMES) window = { kind: 'recent', frames: w.frames }
  else return bad('window')
  const c = input.choices
  if (!isRecord(c) || typeof c.variable !== 'string' || !validVariable(c.variable)) return bad('choices')
  const choices = parseJobChoices(c, [c.variable])
  if (!choices) return bad('choices')
  return {
    ok: true,
    value: {
      title,
      endpoint: normaliseEndpoint(input.endpoint as string),
      bucket: input.bucket,
      prefix,
      region,
      accessKeyId: input.accessKeyId,
      secretAccessKey: input.secretAccessKey,
      schedule,
      window,
      choices,
    },
  }
}
